/**
 * User-reported tickets (hardening plan Phase 3, 3g).
 *
 * The operator SPA creates tickets (`shell/report/ReportProblemHost.tsx`), lists them
 * back on the Support screen, and since 2026-09-06 lets the reporter
 * OPEN one: read what they submitted, follow the conversation, and
 * reply. Triage -- state transitions, assignment, confirming
 * correlations -- still lives in the platform support console.
 *
 * The reply path deliberately sends no visibility. The server decides,
 * and forces `requester_visible` for anyone outside the Nexsoss triage
 * roles; before that, a reporter's own message defaulted to `internal`
 * and was hidden from them on their own ticket.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { ticketDetailKey, ticketsKey } from '../scope';
import {
  addSupportTicketComment,
  createSupportTicket,
  fetchSupportTicket,
  fetchSupportTickets,
} from '../v1-client';
import type {
  CreateSupportTicketRequest,
  SiteId,
  SupportTicket,
  SupportTicketComment,
  SupportTicketDetail,
} from '../v1';

export function useSupportTickets() {
  const siteId = useSafeSiteId();
  return useQuery<SupportTicket[]>({
    queryKey: ticketsKey(siteId),
    queryFn: () => fetchSupportTickets(siteId as SiteId),
    enabled: Boolean(siteId),
  });
}

export function useCreateSupportTicket() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<SupportTicket, Error, CreateSupportTicketRequest>({
    mutationFn: (body) => createSupportTicket(siteId as SiteId, body),
    // Without this the reporter files a ticket, opens the Support tab,
    // and sees a stale list that does not contain it -- which is
    // indistinguishable from the submission having failed.
    onSuccess: () => qc.invalidateQueries({ queryKey: ticketsKey(siteId) }),
  });
}

/**
 * One ticket with its conversation.
 *
 * Polls, because the whole point is seeing a reply arrive. 30s matches
 * what the support console uses on the other side of the same table --
 * fast enough that a person waiting sees an answer without reloading,
 * slow enough to be free.
 */
export function useSupportTicket(ticketId: string | null) {
  const siteId = useSafeSiteId();
  return useQuery<SupportTicketDetail>({
    queryKey: ticketDetailKey(siteId, ticketId ?? ''),
    queryFn: () => fetchSupportTicket(siteId as SiteId, ticketId as string),
    enabled: Boolean(siteId && ticketId),
    refetchInterval: 30_000,
  });
}

export function useAddSupportTicketComment(ticketId: string | null) {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<SupportTicketComment, Error, string>({
    mutationFn: (body) => addSupportTicketComment(siteId as SiteId, ticketId as string, body),
    // Both keys: the thread so the message appears, and the LIST so the
    // "Last update" column moves -- the server now bumps the ticket on
    // every comment, and a list still showing yesterday's timestamp
    // beside a conversation from a moment ago reads as a failed send.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketDetailKey(siteId, ticketId ?? '') });
      void qc.invalidateQueries({ queryKey: ticketsKey(siteId) });
    },
  });
}
